import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { AbComparisonComponent } from 'app/shared/components/ab-comparison/ab-comparison.component';
import { AlertComponent } from 'app/shared/components/alert/alert.component';
import {
  AbPreviewMode,
  PhotoGenerationConfig,
  ProcessedImage,
  ProcessingStatus,
} from '../../models/wizard.model';
import { PhotoAdvancedPanelComponent } from '../photo-advanced-panel/photo-advanced-panel.component';

export interface PreviewModeOption {
  value: AbPreviewMode;
  label: string;
}

@Component({
  selector: 'casa-doc-generation-step',
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    AbComparisonComponent,
    AlertComponent,
    PhotoAdvancedPanelComponent,
  ],
  templateUrl: './generation-step.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenerationStepComponent {
  processedImages = input<ProcessedImage[]>([]);
  previewModes = input<PreviewModeOption[]>([]);
  previewMode = input<AbPreviewMode>('slider');
  canDownload = input<boolean>(false);
  hasPending = input<boolean>(false);
  quotaExhausted = input<boolean>(false);
  /** True mentre è in corso una generazione (batch o singola): blocca le azioni. */
  isGenerating = input<boolean>(false);
  limitWarning = input<string | null>(null);
  showPromptDebug = input<boolean>(false);
  promptPreviews = input<Record<number, string>>({});
  addCompanyLogo = input<boolean>(true);
  showCompanyOption = input<boolean>(false);

  back = output<void>();
  restart = output<void>();
  previewModeChange = output<AbPreviewMode>();
  download = output<void>();
  processAll = output<void>();
  generateOne = output<number>();
  configChange = output<{ index: number; config: PhotoGenerationConfig }>();
  editImage = output<{ index: number; editPrompt: string }>();
  addCompanyLogoChange = output<boolean>();

  expandedPanels = signal<ReadonlySet<number>>(new Set());
  editPrompts = signal<Record<number, string>>({});
  /**
   * "Genera tutte" è un'azione una tantum: dopo il primo click sparisce per
   * sempre (le foto residue si rigenerano singolarmente). Si azzera solo
   * rimontando lo step (es. dopo "Ricomincia").
   */
  processAllClicked = signal<boolean>(false);
  /** Override del confronto per singola foto; fallback al default globale. */
  imagePreviewModes = signal<Record<number, AbPreviewMode>>({});

  /**
   * Imposta la modalità di preview globale e forza la sincronizzazione di tutte
   * le foto. Se la modalità è già quella attiva, ripulisce comunque gli override
   * per-foto (ri-sync forzato).
   */
  setGlobalPreviewMode(mode: AbPreviewMode): void {
    this.imagePreviewModes.set({});
    this.previewModeChange.emit(mode);
  }

  /** Foto generate con successo (per la barra di download). */
  doneCount = computed(
    () => this.processedImages().filter((i) => i.status === 'done').length
  );
  totalCount = computed(() => this.processedImages().length);
  /** Foto ancora da generare o in errore (per il CTA "Genera tutte"). */
  pendingCount = computed(
    () =>
      this.processedImages().filter(
        (i) => i.status === 'pending' || i.status === 'error'
      ).length
  );

  previewModeFor(index: number): AbPreviewMode {
    return this.imagePreviewModes()[index] ?? this.previewMode();
  }

  setImagePreviewMode(index: number, mode: AbPreviewMode) {
    this.imagePreviewModes.update((current) => ({ ...current, [index]: mode }));
  }

  isExpanded(index: number): boolean {
    return this.expandedPanels().has(index);
  }

  promptPreview(index: number): string {
    return this.promptPreviews()[index] ?? '';
  }

  toggleAdvanced(index: number) {
    this.expandedPanels.update((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  editPrompt(index: number): string {
    return this.editPrompts()[index] ?? '';
  }

  setEditPrompt(index: number, value: string) {
    this.editPrompts.update((current) => ({ ...current, [index]: value }));
  }

  submitEdit(index: number) {
    const prompt = this.editPrompt(index).trim();
    if (!prompt) return;
    this.editImage.emit({ index, editPrompt: prompt });
    this.setEditPrompt(index, '');
  }

  triggerProcessAll() {
    this.processAllClicked.set(true);
    this.processAll.emit();
  }

  onToggleCompanyLogo(event: Event) {
    this.addCompanyLogoChange.emit((event.target as HTMLInputElement).checked);
  }

  /**
   * Collasso per indice. Default aperto: il corpo si chiude solo se l'utente
   * lo collassa esplicitamente dall'header.
   */
  cardCollapseOverrides = signal<Record<number, boolean>>({});

  isCardCollapsed(img: ProcessedImage): boolean {
    return this.cardCollapseOverrides()[img.index] ?? false;
  }

  toggleCard(img: ProcessedImage) {
    const collapsed = this.isCardCollapsed(img);
    this.cardCollapseOverrides.update((overrides) => ({
      ...overrides,
      [img.index]: !collapsed,
    }));
  }

  statusLabel(status: ProcessingStatus): string {
    switch (status) {
      case 'done':
        return 'Generata';
      case 'processing':
        return 'Generazione in corso…';
      case 'error':
        return 'Errore';
      default:
        return 'Da generare';
    }
  }
}
